import { ClearButton } from "../clear-button/ClearButton.js";
import { twMerge } from "tailwind-merge";
import { jsx } from "react/jsx-runtime";
import { createContext, use, useMemo, useRef, useState } from "react";
import { bindForwardedRef } from "@givebutter/react-util";
//#region src/input/Input.tsx
const InputContext = createContext({
	isError: false,
	disabled: false,
	isFocused: false,
	inputRef: null,
	enableDisabledStyle: true,
	/* v8 ignore next 2 */
	setIsFocused: () => {},
	setInputRef: () => {}
});
const InputRoot = ({ ref, className, children, isError = false, disabled = false, enableDisabledStyle = true, size = "md", onMouseDown, onFocus, onClick, ...rest }) => {
	const internalRef = useRef(null);
	const [isFocused, setIsFocused] = useState(false);
	const [inputRef, setInputRef] = useState(null);
	const ctx = useMemo(() => ({
		disabled,
		enableDisabledStyle,
		isError,
		isFocused,
		inputRef,
		setInputRef,
		setIsFocused
	}), [
		disabled,
		enableDisabledStyle,
		inputRef,
		isError,
		isFocused
	]);
	return /* @__PURE__ */ jsx(InputContext, {
		value: ctx,
		children: /* @__PURE__ */ jsx("div", {
			ref: (node) => {
				internalRef.current = node;
				bindForwardedRef(ref, node);
			},
			onMouseDown: (e) => {
				if (e.target !== inputRef) e.preventDefault();
				/* v8 ignore next */
				onMouseDown?.(e);
			},
			onClick: (e) => {
				if (e.target !== inputRef) inputRef?.focus();
				/* v8 ignore next */
				onClick?.(e);
			},
			onFocus: (e) => {
				if (e.target === inputRef) inputRef?.focus();
				/* v8 ignore next */
				onFocus?.(e);
			},
			className: twMerge("flex h-fit min-h-6 w-[inherit] cursor-text items-center gap-2 rounded-lg bg-white px-3 text-gray-900 ring-1 ring-gray-300 ring-inset", size === "sm" && "py-1", size === "md" && "py-2", size === "lg" && "py-2.5", enableDisabledStyle && disabled && "cursor-not-allowed bg-gray-50 text-gray-300", (isError || isFocused) && "ring-2", isError && "ring-red-500", isFocused && "ring-blue-500", className),
			...rest,
			children
		})
	});
};
const InputForm = ({ ref, className, disabled, onFocus, onBlur, ...rest }) => {
	const { setIsFocused, disabled: disabledCtx, enableDisabledStyle, isError, setInputRef } = use(InputContext);
	return /* @__PURE__ */ jsx("input", {
		ref: (node) => {
			bindForwardedRef(ref, node);
			setInputRef(node);
		},
		disabled: disabled || disabledCtx,
		className: twMerge("h-full min-h-6 max-w-full grow appearance-none border-none bg-transparent outline-hidden", enableDisabledStyle && "disabled:cursor-not-allowed disabled:placeholder:text-gray-200", "placeholder:text-gray-400", className),
		onFocus: (e) => {
			setIsFocused(true);
			/* v8 ignore next */
			onFocus?.(e);
		},
		onBlur: (e) => {
			setIsFocused(false);
			/* v8 ignore next */
			onBlur?.(e);
		},
		"aria-invalid": isError || void 0,
		...rest
	});
};
const InputInteractable = ({ children, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		onMouseDown: (e) => e.stopPropagation(),
		onClick: (e) => e.stopPropagation(),
		...rest,
		children
	});
};
const InputClearButton = ({ visible, onClick, "aria-label": ariaLabel = "Clear", ...rest }) => {
	const { inputRef } = use(InputContext);
	return /* @__PURE__ */ jsx(InputInteractable, {
		className: twMerge("flex items-center", !visible && "invisible"),
		children: /* @__PURE__ */ jsx(ClearButton, {
			"aria-label": ariaLabel,
			"aria-hidden": !visible,
			tabIndex: visible ? void 0 : -1,
			onClick: (event) => {
				onClick(event);
				inputRef?.focus();
			},
			...rest
		})
	});
};
const Root = InputRoot;
const Form = InputForm;
const Interactable = InputInteractable;
const ClearButtonAlias = InputClearButton;
//#endregion
export { ClearButtonAlias as ClearButton, Form, InputClearButton, InputContext, InputForm, InputInteractable, InputRoot, Interactable, Root };
